/**
 * SMS Marketing Service
 * Handles SMS campaign management, templates, and sending
 */
import api from './api';

const smsService = {
  // ==================== Campaigns ====================
  /**
   * Get all SMS campaigns
   */
  getCampaigns: async (params = {}) => {
    const response = await api.get('/sms/campaigns', { params });
    return response.data;
  },

  /**
   * Get campaign by ID
   */
  getCampaign: async (campaignId) => {
    const response = await api.get(`/sms/campaigns/${campaignId}`);
    return response.data;
  },

  /**
   * Create SMS campaign
   */
  createCampaign: async (campaignData) => {
    const response = await api.post('/sms/campaigns', campaignData);
    return response.data;
  },

  /**
   * Update SMS campaign
   */
  updateCampaign: async (campaignId, campaignData) => {
    const response = await api.put(`/sms/campaigns/${campaignId}`, campaignData);
    return response.data;
  },

  /**
   * Delete SMS campaign
   */
  deleteCampaign: async (campaignId) => {
    const response = await api.delete(`/sms/campaigns/${campaignId}`);
    return response.data;
  },

  /**
   * Send SMS campaign
   */
  sendCampaign: async (campaignId, scheduleData = {}) => {
    const response = await api.post(`/sms/campaigns/${campaignId}/send`, scheduleData);
    return response.data;
  },

  /**
   * Schedule SMS campaign
   */
  scheduleCampaign: async (campaignId, scheduleTime) => {
    const response = await api.post(`/sms/campaigns/${campaignId}/schedule`, {
      scheduled_at: scheduleTime
    });
    return response.data;
  },

  /**
   * Cancel scheduled campaign
   */
  cancelScheduled: async (campaignId) => {
    const response = await api.post(`/sms/campaigns/${campaignId}/cancel`);
    return response.data;
  },

  // ==================== Templates ====================
  /**
   * Get all SMS templates
   */
  getTemplates: async (params = {}) => {
    const response = await api.get('/sms/templates', { params });
    return response.data;
  },

  /**
   * Get template by ID
   */
  getTemplate: async (templateId) => {
    const response = await api.get(`/sms/templates/${templateId}`);
    return response.data;
  },

  /**
   * Create SMS template
   */
  createTemplate: async (templateData) => {
    const response = await api.post('/sms/templates', templateData);
    return response.data;
  },

  /**
   * Update SMS template
   */
  updateTemplate: async (templateId, templateData) => {
    const response = await api.put(`/sms/templates/${templateId}`, templateData);
    return response.data;
  },

  /**
   * Delete SMS template
   */
  deleteTemplate: async (templateId) => {
    const response = await api.delete(`/sms/templates/${templateId}`);
    return response.data;
  },

  /**
   * Preview template with test data
   */
  previewTemplate: async (templateId, testData = {}) => {
    const response = await api.post(`/sms/templates/${templateId}/preview`, testData);
    return response.data;
  },

  // ==================== Recipients ====================
  /**
   * Get recipients for campaign
   */
  getCampaignRecipients: async (campaignId, params = {}) => {
    const response = await api.get(`/sms/campaigns/${campaignId}/recipients`, { params });
    return response.data;
  },

  /**
   * Add recipients to campaign
   */
  addRecipients: async (campaignId, recipients) => {
    const response = await api.post(`/sms/campaigns/${campaignId}/recipients`, {
      recipients
    });
    return response.data;
  },

  /**
   * Remove recipient from campaign
   */
  removeRecipient: async (campaignId, recipientId) => {
    const response = await api.delete(`/sms/campaigns/${campaignId}/recipients/${recipientId}`);
    return response.data;
  },

  /**
   * Import recipients from file
   */
  importRecipients: async (campaignId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/sms/campaigns/${campaignId}/recipients/import`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // ==================== Analytics ====================
  /**
   * Get SMS analytics overview
   */
  getAnalyticsOverview: async (params = {}) => {
    const response = await api.get('/sms/analytics/overview', { params });
    return response.data;
  },

  /**
   * Get campaign analytics
   */
  getCampaignAnalytics: async (campaignId, params = {}) => {
    const response = await api.get(`/sms/campaigns/${campaignId}/analytics`, { params });
    return response.data;
  },

  /**
   * Get delivery report
   */
  getDeliveryReport: async (campaignId) => {
    const response = await api.get(`/sms/campaigns/${campaignId}/delivery-report`);
    return response.data;
  },

  // ==================== Credits ====================
  /**
   * Get SMS credits balance
   */
  getCreditsBalance: async () => {
    const response = await api.get('/sms/credits');
    return response.data;
  },

  /**
   * Get credits usage history
   */
  getCreditsHistory: async (params = {}) => {
    const response = await api.get('/sms/credits/history', { params });
    return response.data;
  },

  // ==================== Opt-out Management ====================
  /**
   * Get opt-out list
   */
  getOptOutList: async (params = {}) => {
    const response = await api.get('/sms/opt-out', { params });
    return response.data;
  },

  /**
   * Add to opt-out list
   */
  addToOptOut: async (phoneNumber, reason = '') => {
    const response = await api.post('/sms/opt-out', {
      phone_number: phoneNumber,
      reason
    });
    return response.data;
  },

  /**
   * Remove from opt-out list
   */
  removeFromOptOut: async (phoneNumber) => {
    const response = await api.delete('/sms/opt-out', {
      data: { phone_number: phoneNumber }
    });
    return response.data;
  }
};

export default smsService;
